import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useBusinessConnectors, useConnectedApps } from '@/api/connected-apps';
import { CONNECTED_APPS_EVENTS } from '@/analytics/connected-apps';
import { APP_ROUTE } from '@/constants';
import { trackEvent } from '@/helpers/external-tools-helper';
import { useDemoMode } from '@/hooks/use-demo-mode';
import { ClientContainer, UserContainer } from '@/providers';
import { mcpAnnouncementStorage } from '@/utils/mcp-announcement-storage';

export const MCP_ANNOUNCEMENT_FLAG = 'mcp-announcement';

/**
 * A dialog takes the screen, so it should arrive with the page rather than
 * interrupt someone who has already started working — but not before the page
 * has painted.
 */
export const MCP_ANNOUNCEMENT_DELAY_MS = 1000;

/**
 * Decides whether to tell this person they can connect an AI assistant.
 *
 * It is for people who already use ViewMetrics and have not connected one. The
 * cheap checks run first and gate the two requests, so someone who will never
 * see the announcement costs no extra network calls on every page.
 */
export const useMcpAnnouncement = ({ suppressed = false } = {}) => {
  const posthog = usePostHog();
  const location = useLocation();
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const { isDemoMode } = useDemoMode();

  const userId = userState.user?.id;
  const businessId = userState.business?.id;
  const clientId = clientState.selectedClient?.id;

  const [flagEnabled, setFlagEnabled] = useState(false);
  const [seen, setSeen] = useState(true);
  const [delayElapsed, setDelayElapsed] = useState(false);

  useEffect(() => {
    if (!posthog) return undefined;

    return posthog.onFeatureFlags(() =>
      setFlagEnabled(posthog.isFeatureEnabled(MCP_ANNOUNCEMENT_FLAG) === true)
    );
  }, [posthog]);

  useEffect(() => {
    setSeen(userId ? mcpAnnouncementStorage.hasSeen(userId) : true);
  }, [userId]);

  const onConnectedAppsPage = location.pathname.includes(
    APP_ROUTE.CONNECTED_APPS
  );

  // Finding the page on their own counts as having been told.
  useEffect(() => {
    if (onConnectedAppsPage && userId && !seen) {
      mcpAnnouncementStorage.markSeen(userId);
      setSeen(true);
    }
  }, [onConnectedAppsPage, userId, seen]);

  const mayAsk =
    flagEnabled &&
    // Another dialog has the screen. Wait; do not stack on it or count as seen.
    !suppressed &&
    !seen &&
    !isDemoMode &&
    !onConnectedAppsPage &&
    // Still setting up: they have a first data source to connect before this.
    userState.business?.activationFlow === true &&
    Boolean(businessId);

  const connectors = useBusinessConnectors(mayAsk ? businessId : undefined);
  const connectedApps = useConnectedApps({ enabled: mayAsk });

  const eligible =
    mayAsk &&
    // No data means no MCP in this deployment, or a failed request: stay quiet.
    connectors.data?.disabled === false &&
    connectedApps.data !== undefined &&
    !connectedApps.data.some(app => app.youAreConnected);

  useEffect(() => {
    if (!eligible) return undefined;

    const timer = setTimeout(
      () => setDelayElapsed(true),
      MCP_ANNOUNCEMENT_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [eligible]);

  const visible = eligible && delayElapsed;

  useEffect(() => {
    if (visible) {
      trackEvent(CONNECTED_APPS_EVENTS.MCP_ANNOUNCEMENT_SHOWN, {
        businessId,
        clientId,
      });
    }
    // Once per appearance, not once per id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const close = useCallback(
    (event: string) => {
      if (userId) mcpAnnouncementStorage.markSeen(userId);
      setSeen(true);
      trackEvent(event, { businessId, clientId });
    },
    [userId, businessId, clientId]
  );

  return {
    visible,
    setupPath: `/ws/${clientId}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.CONNECTED_APPS}`,
    accept: () => close(CONNECTED_APPS_EVENTS.MCP_ANNOUNCEMENT_ACCEPTED),
    dismiss: () => close(CONNECTED_APPS_EVENTS.MCP_ANNOUNCEMENT_DISMISSED),
  };
};
